/**
 * Types and pure helpers for GET /analytics/memory-reads/summary (Redis aggregates).
 */

export type MemoryReadTimeRange = '7d' | '30d' | '90d';

export interface MemoryReadRollupRow {
  operation: string;
  integration_type: string;
  space_id: string | null;
  outcome: string;
  count: number;
}

export interface MemoryReadByDay {
  date: string;
  total_reads: number;
  breakdown: MemoryReadRollupRow[];
}

export interface MemoryReadSummaryResponse {
  time_range_days: number;
  total_reads: number;
  rollup: MemoryReadRollupRow[];
  by_day: MemoryReadByDay[];
  source: string;
  user_id?: string;
  space_id_filter?: string | null;
}

/** Outcomes that count as “relaxed” success (usable handler outcome, possibly empty). */
const RELAXED_SUCCESS_OUTCOMES = new Set([
  'success',
  'empty_result',
  'policy_blocked',
]);

export interface MemoryReadRollupSummary {
  byOutcome: Record<string, number>;
  /** Sum of counts in rollup (denominator for rates). */
  totalFromRollup: number;
  strictSuccessCount: number;
  relaxedSuccessCount: number;
  strictRate: number | null;
  relaxedRate: number | null;
  /** Top rows by volume for the detail table. */
  topRollupByCount: MemoryReadRollupRow[];
}

export function summarizeMemoryReadRollup(
  rollup: MemoryReadRollupRow[]
): MemoryReadRollupSummary {
  const byOutcome: Record<string, number> = {};
  let totalFromRollup = 0;

  for (const row of rollup) {
    const c = Number(row.count) || 0;
    if (c <= 0) continue;
    totalFromRollup += c;
    const o = row.outcome || 'unknown';
    byOutcome[o] = (byOutcome[o] ?? 0) + c;
  }

  const strictSuccessCount = byOutcome.success ?? 0;
  let relaxedSuccessCount = 0;
  for (const [outcome, n] of Object.entries(byOutcome)) {
    if (RELAXED_SUCCESS_OUTCOMES.has(outcome)) {
      relaxedSuccessCount += n;
    }
  }

  const strictRate =
    totalFromRollup > 0 ? (strictSuccessCount / totalFromRollup) * 100 : null;
  const relaxedRate =
    totalFromRollup > 0 ? (relaxedSuccessCount / totalFromRollup) * 100 : null;

  const topRollupByCount = [...rollup]
    .filter((r) => (Number(r.count) || 0) > 0)
    .sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0))
    .slice(0, 5);

  return {
    byOutcome,
    totalFromRollup,
    strictSuccessCount,
    relaxedSuccessCount,
    strictRate,
    relaxedRate,
    topRollupByCount,
  };
}
